import type { UseFormReturn } from 'react-hook-form';
import { FormSection } from '@core/components';
import type { Vestment } from '../../api/vestment';
import type { ChasubleFeatures } from '../utils/chasuble';
import {
  type DesignPresetFormValues,
  type PresetFeatures,
} from '../utils/designPreset';
import type { StoleFeatures } from '../utils/stole';
import {
  ChasubleBodyFabricFields,
  ChasubleCollarFields,
  ChasublePanelMaterialFields,
  ChasublePanelTextFields,
} from './ChasubleValueFields';
import { StoleOptionFields, StolePanelFields } from './StoleValueFields';

interface DesignPresetFieldsProps {
  methods: UseFormReturn<DesignPresetFormValues>;
  features: PresetFeatures;
  vestment?: Vestment;
}

export function DesignPresetFields({
  methods,
  features,
  vestment,
}: DesignPresetFieldsProps) {
  if (features.kind === 'stole') {
    return (
      <StoleDesignPresetFields
        features={features.features}
        vestment={vestment}
      />
    );
  }
  return (
    <ChasubleDesignPresetFields
      methods={methods}
      features={features.features}
      collarVestment={vestment}
    />
  );
}

function StoleDesignPresetFields({
  features,
  vestment,
}: {
  features: StoleFeatures;
  vestment?: Vestment;
}) {
  return (
    <>
      <FormSection title="Panel">
        <StolePanelFields<DesignPresetFormValues>
          hasCuts={features.hasCuts}
          cuts={vestment?.stoleCuts ?? []}
        />
      </FormSection>
      <FormSection title="Options">
        <StoleOptionFields<DesignPresetFormValues>
          patching={features.patching}
        />
      </FormSection>
    </>
  );
}

function ChasubleDesignPresetFields({
  methods,
  features,
  collarVestment,
}: {
  methods: UseFormReturn<DesignPresetFormValues>;
  features: ChasubleFeatures;
  collarVestment?: Vestment;
}) {
  return (
    <>
      <FormSection title="Body">
        <ChasubleBodyFabricFields<DesignPresetFormValues> />
      </FormSection>
      {features.panel ? (
        <FormSection title="Panel">
          <ChasublePanelMaterialFields methods={methods} source={features}>
            {(fields) => (
              <ChasublePanelTextFields<DesignPresetFormValues>
                fields={fields}
              />
            )}
          </ChasublePanelMaterialFields>
        </FormSection>
      ) : null}
      {features.collar ? (
        <FormSection title="Collar">
          <ChasubleCollarFields<DesignPresetFormValues>
            vestment={collarVestment}
            showType={collarVestment != null}
          />
        </FormSection>
      ) : null}
    </>
  );
}
